/**
 * ReportCardNudgeBanner.jsx — Feb 2026 mid-month.
 *
 * Seasonal report-card nudge banner. Renders on the FREE-user home
 * (`audience="free"`) or the BASIC-user paid dashboard home
 * (`audience="basic"`) — same 4-way admin switch drives both, but the
 * copy is audience-specific. Self-hides when the switch is "off", when
 * the API errors, or when no message is defined for this audience.
 *
 * Audience-eligibility guards live at the MOUNT site (FreeHome and
 * PaidDashboard `TabsContent value="home"`). This component trusts its
 * `audience` prop — it does NOT re-inspect the user object. Keeps the
 * component reusable if the founder later adds a Standard tier that
 * SHOULD see a nudge (unlikely, but future-safe).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar } from "lucide-react";
import { fetchReportCardNudge } from "../lib/api";
import {
  getNudgeMessage,
  DEFAULT_NUDGE_POSITION,
} from "./reportCardNudge";

const ReportCardNudgeBanner = ({ audience }) => {
  const [position, setPosition] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetchReportCardNudge()
      .then((data) => {
        if (cancelled) return;
        setPosition((data && data.position) || DEFAULT_NUDGE_POSITION);
      })
      .catch(() => {
        if (!cancelled) setPosition(DEFAULT_NUDGE_POSITION);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!position || position === "off") return null;
  const msg = getNudgeMessage(position, audience);
  if (!msg) return null;

  return (
    <div
      data-testid={`report-card-nudge-banner-${audience}`}
      data-nudge-position={position}
      className="rounded-2xl border border-[#96ded1]/60 bg-[#1c2d3d] text-white p-5 sm:p-6 shadow-sm"
    >
      <div className="flex items-start gap-4 flex-col sm:flex-row">
        <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#96ded1]/25 text-[#96ded1] shrink-0">
          <Calendar className="h-5 w-5" strokeWidth={2.2} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] sm:text-[11px] font-extrabold uppercase tracking-[0.22em] text-[#96ded1]">
            Report Card Season
          </p>
          <p
            data-testid={`report-card-nudge-body-${audience}`}
            className="mt-2 text-base sm:text-lg text-white leading-relaxed"
          >
            <span className="font-black">{msg.bold}</span>{" "}
            <span className="text-white/85 font-medium">{msg.body}</span>
          </p>
          <Link
            to="/pricing"
            data-testid={`report-card-nudge-cta-${audience}`}
            className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-[#96ded1] px-4 py-2 text-xs font-extrabold uppercase tracking-[0.14em] text-[#1c2d3d] hover:bg-white transition-colors"
          >
            See plans
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ReportCardNudgeBanner;
